/**
 * Shared TypeScript types.
 */

/**
 * A moment's decoration, as data rather than pixels (FEAT-028 wave 2).
 * Defined beside the renderer that draws it — there is exactly one of each.
 */
export type {
  MomentOverlays,
  OverlayBar,
  OverlayStroke,
} from '../utils/overlayRender';
import type { MomentOverlays } from '../utils/overlayRender';

// User types
export interface User {
  id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  is_verified: boolean;
  created_at: string;
}

export interface UserPrivate extends User {
  email: string;
  is_active: boolean;
  is_admin: boolean;
  last_login_at: string | null;
  updated_at: string;
}

// Message types
export type MessageType = 'snap' | 'text';
export type MediaType = 'photo' | 'video';
export type RetentionType = 'view_once' | '24h' | '7d' | 'forever';
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'viewed' | 'expired';

// Extended status for optimistic UI (client-side only)
export type OptimisticStatus = MessageStatus | 'failed' | 'queued';

export interface Message {
  id: number;
  sender_id: number;
  recipient_id: number;
  message_type: MessageType;
  text_content: string | null;
  media_type: MediaType | null;
  media_url: string | null;
  thumbnail_url: string | null;
  is_hd?: boolean;
  thumbhash?: string | null;
  /** Delivery size in bytes (FEAT-028 wave 7) — null on pre-wave rows. */
  media_size_bytes?: number | null;
  caption: string | null;
  status: MessageStatus;
  retention_type: RetentionType;
  expires_at: string | null;
  viewed_at: string | null;
  replay_allowed: boolean;
  replay_enabled_at: string | null;
  replayed_at: string | null;
  saved_to_vault: boolean;
  /**
   * FEAT-040 w2, viewer-INDEPENDENT: "the RECIPIENT of this message may save
   * it" (the sender's grant, never view-once). Own-sent rows carry it false —
   * the viewer's own-moment rule (sender_id === my id) shows their button.
   */
  can_save?: boolean;
  /**
   * The grey bar and the drawing, as DATA (FEAT-028 wave 2). NULL means the
   * decoration is already in the pixels (every moment sent before wave 2) or
   * there wasn't any — either way the renderer draws nothing, which is what
   * keeps a moment from wearing its caption twice.
   */
  overlays?: MomentOverlays | null;
  created_at: string;
}

/**
 * Optimistic message - shown immediately before server confirms.
 * Uses string ID (temp_xxx) until replaced with real server ID.
 */
export interface OptimisticMessage {
  id: string; // temp_xxx format
  sender_id: number;
  recipient_id: number;
  message_type: MessageType;
  text_content: string | null;
  media_type: MediaType | null;
  media_url: string | null; // local blob URL for snaps
  thumbnail_url: string | null;
  is_hd?: boolean;
  thumbhash?: string | null;
  /** Never set on an optimistic row — present so DisplayMessage narrows. */
  media_size_bytes?: number | null;
  caption: string | null;
  status: OptimisticStatus;
  retention_type: RetentionType;
  expires_at: string | null;
  viewed_at: null;
  replay_allowed: boolean;
  replayed_at: null;
  saved_to_vault: false;
  created_at: string;
  // Optimistic-specific fields
  _isOptimistic: true;
  _localMediaBlob?: Blob; // Keep blob for retry (snaps only)
  /** Kept for the same reason as the blob: a retry has to send the grey bar
   *  again too, and since FEAT-028 wave 2 it is not inside those bytes. */
  _localOverlays?: MomentOverlays | null;
  _localThumbnailBlob?: Blob;
  _error?: string; // Error message if failed
  _clientKey?: string; // BUG-055: retries replay this so the server converges
}

// Union type for display - can be real or optimistic
export type DisplayMessage = Message | OptimisticMessage;

/** BUG-055: how a send resolved. Compare with === 'sent' — never truthiness
 * ('failed' is truthy). 'queued' means the durable outbox owns it now. */
export type SendOutcome = 'sent' | 'queued' | 'failed';

// Type guard to check if message is optimistic
export function isOptimisticMessage(msg: DisplayMessage): msg is OptimisticMessage {
  return '_isOptimistic' in msg && msg._isOptimistic === true;
}

export interface SendMessageData {
  recipient_id: number;
  media_type: MediaType;
  caption?: string;
  retention_type?: RetentionType;
  replay_allowed?: boolean;
  media: Blob;
  thumbnail?: Blob;
  hd?: boolean; // sharper delivery rendition (photos)
  /** FEAT-028 wave 2: the grey bar and drawing, as data beside the bytes. */
  overlays?: MomentOverlays | null;
  staged_media_id?: string; // FEAT-012: claim an early upload instead of re-sending bytes
  /** FEAT-028 wave 4: more destinations are coming for this capture, so the
   *  server copies the staged upload instead of consuming it. */
  keep_staged?: boolean;
  /** BUG-115: re-share a Memory the server already stores — it copies the
   *  files itself instead of the phone uploading them back, once per
   *  recipient. The blob still rides along for the optimistic bubble and
   *  for the outbox, which has to stay durable when a send fails. */
  saved_item_id?: number;
  client_key?: string; // BUG-055: idempotency key — every retry path replays it
  draft_id?: string; // FEAT-017: the capture this send delivers (outbox removes it on confirm)
  facing?: CameraFacing; // FEAT-024: which camera took it — omitted when unknown
}

// Conversation types

/**
 * FEAT-024: the last thing that happened in a conversation, read off the
 * content-free receipts ledger. It outlives the moment it describes, which is
 * the point — `last_message` goes empty exactly when a conversation has been
 * most alive, because a watched moment is deleted within seconds (BUG-078).
 * Never carries a word of what was said.
 */
export interface LastEvent {
  kind: 'moment' | 'text';
  direction: 'sent' | 'received';
  status: 'sent' | 'delivered' | 'opened' | 'expired';
  at: string;
}

export interface ConversationPreview {
  friend_id: number;
  friend: User;
  last_message: Message | null;
  last_event: LastEvent | null;
  streak_count: number;
  streak_at_risk: boolean;
  unread_count: number;
  last_activity: string;
}

// Story types
export interface StoryViewer {
  id: number;
  viewer: User;
  viewed_at: string;
}

export interface StoryReaction {
  id: number;
  user: User;
  emoji: string;
  created_at: string;
}

export interface Story {
  id: number;
  user_id: number;
  user: User;
  media_type: MediaType;
  media_url: string;
  thumbnail_url: string | null;
  is_hd?: boolean;
  thumbhash?: string | null;
  /** Delivery size in bytes (FEAT-028 wave 7) — null on pre-wave rows. */
  media_size_bytes?: number | null;
  caption: string | null;
  is_private: boolean;
  /** Owner-only — null on other people's stories. */
  view_count: number | null;
  /** Multi-shot: shots posted together share a batch, ordered by position. */
  batch_id: string | null;
  batch_position: number | null;
  /** Decoration as data — see `Message.overlays` (FEAT-028 wave 2). */
  overlays?: MomentOverlays | null;
  expires_at: string;
  created_at: string;
  viewed: boolean;
  // Optional - populated in detail views
  viewers?: StoryViewer[];
  reactions?: StoryReaction[];
}

export interface StoryWithViewed {
  id: number;
  user_id: number;
  user: User;
  media_type: MediaType;
  media_url: string;
  thumbnail_url: string | null;
  is_hd?: boolean;
  thumbhash?: string | null;
  /** Delivery size in bytes (FEAT-028 wave 7) — null on pre-wave rows. */
  media_size_bytes?: number | null;
  caption: string | null;
  is_private: boolean;
  view_count: number | null;
  batch_id: string | null;
  batch_position: number | null;
  /** Decoration as data — see `Message.overlays` (FEAT-028 wave 2). */
  overlays?: MomentOverlays | null;
  expires_at: string;
  created_at: string;
  viewed: boolean;
}

export interface UserStoryGroup {
  user: User;
  stories: StoryWithViewed[];
  has_unwatched: boolean;
  latest_story_at: string;
}

/** Who a story goes out to. Resolved server-side at post time. */
export type StoryAudience = 'everyone' | 'close_friends' | 'custom';

export interface PostStoryData {
  media: Blob;
  media_type: MediaType;
  caption?: string;
  audience?: StoryAudience;
  private_viewer_ids?: number[]; // only for 'custom'
  batch_id?: string; // multi-shot: shared per batch, with position
  batch_position?: number;
  hd?: boolean; // sharper delivery rendition (photos)
  /** FEAT-028 wave 2: the grey bar and drawing, as data beside the bytes. */
  overlays?: MomentOverlays | null;
  staged_media_id?: string; // FEAT-012: claim an early upload instead of re-sending bytes
  /** FEAT-028 wave 4: more destinations are coming for this capture, so the
   *  server copies the staged upload instead of consuming it. */
  keep_staged?: boolean;
  /** BUG-115: re-share a Memory the server already stores — it copies the
   *  files itself instead of the phone uploading them back, once per
   *  recipient. The blob still rides along for the optimistic bubble and
   *  for the outbox, which has to stay durable when a send fails. */
  saved_item_id?: number;
  client_key?: string; // BUG-055: idempotency key — every retry path replays it
  facing?: CameraFacing; // FEAT-024: which camera took it — omitted when unknown
}

// Friend types
export type FriendRequestStatus = 'pending' | 'accepted' | 'rejected' | 'cancelled';

export interface FriendRequest {
  id: number;
  sender: User;
  receiver: User;
  status: FriendRequestStatus;
  message: string | null;
  created_at: string;
  responded_at: string | null;
}

export interface Friendship {
  id: number;
  friend: User;
  nickname: string | null;
  is_best_friend: boolean;
  /** FEAT-040 w2: MY grant — I let this friend save what I send them. */
  allow_save: boolean;
  created_at: string;
}

export interface Block {
  id: number;
  blocked: User;
  created_at: string;
}

export interface UserSearchResult {
  id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_friend: boolean;
  has_pending_request: boolean;
  request_direction: 'sent' | 'received' | null;
}

export interface FriendStats {
  total_friends: number;
  best_friends: number;
  pending_incoming: number;
  pending_outgoing: number;
}

// Camera types
export type CameraFacing = 'user' | 'environment';
export type FlashMode = 'off' | 'on' | 'auto';
export type CaptureMode = 'photo' | 'video';

export interface CapturedMedia {
  /**
   * Stable identity, minted the instant the media exists — shutter, or the
   * moment a Memory is picked for re-sharing. Keys the on-disk draft
   * (FEAT-017) and every idempotency key a send or save goes out under, so
   * any retry converges instead of duplicating (FEAT-024). Required:
   * optional identity is identity that silently isn't there.
   *
   * Also the staged-upload key since FEAT-028 wave 3: the bytes behind an id
   * never change (wave 2 moved decoration out of them), and the id survives
   * a draft resume where the object URL is re-minted — so one capture is one
   * upload, ever. See services/stagedUpload.ts.
   */
  id: string;
  type: CaptureMode;
  blob: Blob;
  url: string;
  width: number;
  height: number;
  duration?: number; // For video, in seconds
  /**
   * The poster stored beside this moment's labels (FEAT-038 wave 3) — taken
   * from the frame that is already drawn, capped at 24 KB, and what the drafts
   * sheet and the send queue render instead of decoding a video.
   */
  thumbnail?: { bytes: ArrayBuffer; type: string };
  facing?: CameraFacing; // Which camera was used (for video mirroring)
  hd?: boolean; // sharper delivery rendition — always true for photos (BUG-037)
  /**
   * FEAT-028 wave 2: this shot's own decoration, kept BESIDE the bytes rather
   * than burned into them. A queued multi-shot carries its own — every shot in
   * a batch was edited separately, and the bar the second one wears has
   * nothing to do with the first.
   */
  overlays?: MomentOverlays | null;
}

// Vault types
/**
 * A caption bar burned into a moment.
 *
 * MEDIA SPACE — the one coordinate space every overlay in this app lives in.
 * `y` is a percentage of the PICTURE's height (0-100), not of the screen: the
 * editor previews with `object-contain`, so the picture is only part of the
 * screen, and the bake multiplies these numbers straight by the file's pixels
 * (`utils/mediaBaking.ts`). Producers map a finger position into this space
 * through `utils/containRect.ts`; `DrawingPoint` below is the same space as a
 * 0-1 fraction. Break that and overlays land somewhere nobody put them —
 * mildly today, ~3× off the moment landscape captures exist (FEAT-027).
 */
export interface TextBar {
  text: string;
  y: number; // % of the media's height from its top (0-100) — MEDIA SPACE
}

export interface SavedItem {
  id: number;
  media_type: MediaType;
  media_url: string;
  thumbnail_url: string | null;
  thumbhash?: string | null;
  caption: string | null;
  text_bars: TextBar[] | null; // Styled text overlays with positions
  from_user: User | null;
  is_sent_by_me: boolean;
  is_private: boolean;
  is_encrypted: boolean; // Zero-knowledge encrypted (client must decrypt)
  encryption_version: number | null; // 1=symmetric, 2=ECDH hybrid
  album_id: number | null;
  /** Decoration as data — see `Message.overlays` (FEAT-028 wave 2). */
  overlays?: MomentOverlays | null;
  original_created_at: string | null;
  saved_at: string;
}

export interface Album {
  id: number;
  name: string;
  /** Derived cover = the album's newest item; render via LazyThumbnail */
  cover_item_id: number | null;
  cover_thumbhash: string | null;
  is_private: boolean;
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface VaultSettings {
  has_pin: boolean;
  encryption_setup?: boolean; // Zero-knowledge encryption enabled
}

export interface VaultOverview {
  total_items: number;
  private_items: number;
  albums: Album[];
  settings: VaultSettings;
}

// Group types
export type GroupRole = 'owner' | 'admin' | 'member';

export interface GroupMember {
  id: number;
  user: User;
  role: GroupRole;
  nickname: string | null;
  muted: boolean;
  joined_at: string;
}

export interface Group {
  id: number;
  name: string;
  description: string | null;
  avatar_url: string | null;
  owner_id: number;
  owner: User;
  member_count: number;
  allow_member_invite: boolean;
  created_at: string;
  updated_at: string;
}

export interface GroupDetail extends Group {
  members: GroupMember[];
}

export interface GroupMessage {
  id: number;
  group_id: number;
  sender_id: number;
  sender: User;
  media_type: MediaType;
  media_url: string;
  thumbnail_url: string | null;
  is_hd?: boolean;
  thumbhash?: string | null;
  /** Delivery size in bytes (FEAT-028 wave 7) — null on pre-wave rows. */
  media_size_bytes?: number | null;
  caption: string | null;
  /** Decoration as data — see `Message.overlays` (FEAT-028 wave 2). */
  overlays?: MomentOverlays | null;
  created_at: string;
  view_count: number;
  viewed: boolean;
}

export interface GroupWithLastMessage {
  id: number;
  name: string;
  description: string | null;
  avatar_url: string | null;
  member_count: number;
  last_message: GroupMessage | null;
  unread_count: number;
  updated_at: string;
}

export interface CreateGroupData {
  name: string;
  description?: string;
  member_ids: number[];
}

export interface SendGroupMessageData {
  group_id: number;
  media_type: MediaType;
  caption?: string;
  media: Blob;
  thumbnail?: Blob;
  hd?: boolean; // sharper delivery rendition (photos)
  /** FEAT-028 wave 2: the grey bar and drawing, as data beside the bytes. */
  overlays?: MomentOverlays | null;
  staged_media_id?: string; // FEAT-012: claim an early upload instead of re-sending bytes
  /** FEAT-028 wave 4: more destinations are coming for this capture, so the
   *  server copies the staged upload instead of consuming it. */
  keep_staged?: boolean;
  /** BUG-115: re-share a Memory the server already stores — it copies the
   *  files itself instead of the phone uploading them back, once per
   *  recipient. The blob still rides along for the optimistic bubble and
   *  for the outbox, which has to stay durable when a send fails. */
  saved_item_id?: number;
  client_key?: string; // BUG-055: idempotency key — every retry path replays it
  draft_id?: string; // FEAT-017: the capture this send delivers
  facing?: CameraFacing; // FEAT-024: which camera took it — omitted when unknown
}

// =============================================================================
// Bible types
// =============================================================================

export interface BibleTranslation {
  id: number;
  code: string;
  name: string;
  language: string;
  description: string | null;
  is_public_domain: boolean;
}

export interface BibleBook {
  id: number;
  book_number: number;
  name: string;
  abbreviation: string;
  testament: 'Old Testament' | 'New Testament';
  chapter_count: number;
}

export interface VerseHighlight {
  id: number;
  color: string;
  selected_text: string | null; // null = full verse, otherwise partial text
}

export interface BibleVerse {
  id: number;
  verse_number: number;
  text: string;
  is_highlighted: boolean; // True if full verse is highlighted
  highlight_color: string | null; // Color of full verse highlight
  highlights?: VerseHighlight[]; // All highlights (full + partial)
  has_note: boolean;
  is_bookmarked: boolean;
  community_notes_count: number; // Count of public notes from other users
}

export interface CommunityNote {
  id: number;
  verse_id: number;
  content: string;
  created_at: string;
  user_id: number;
  author_name: string;
  author_avatar: string | null;
}

export interface ChapterNavigation {
  book: string;
  book_name: string;
  chapter: number;
}

export interface ChapterVersesResponse {
  success: boolean;
  translation: BibleTranslation;
  book: BibleBook;
  chapter_number: number;
  verses: BibleVerse[];
  prev_chapter: ChapterNavigation | null;
  next_chapter: ChapterNavigation | null;
}

export interface BibleSearchResult {
  id: number;
  reference: string;
  text: string;
  snippet: string;
  book_name: string;
  chapter_number: number;
  verse_number: number;
}

export interface BibleHighlight {
  id: number;
  verse_id: number;
  color: 'yellow' | 'green' | 'blue' | 'pink' | 'purple';
  created_at: string;
  reference?: string;
  text?: string;
}

export interface BibleNote {
  id: number;
  verse_id: number;
  content: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  reference?: string;
  text?: string;
}

export interface BibleBookmark {
  id: number;
  verse_id: number;
  label: string | null;
  created_at: string;
  reference?: string;
  text?: string;
}

// Reading position for resuming
export interface ReadingPosition {
  translation_code: string;
  book_abbreviation: string;
  book_name: string;
  chapter_number: number;
  verse_number?: number;
}

// =============================================================================
// Devotion types
// =============================================================================

export interface DevotionUserInfo {
  id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface DailyDevotion {
  id: number;
  devotion_date: string;
  title: string;
  scripture_reference: string;
  scripture_text: string | null;
  content: string;
  reflection_prompt: string | null;
  author: string | null;
  theme: string | null;
  created_at: string;
  my_reflection: Reflection | null;
}

export interface DevotionSummary {
  id: number;
  devotion_date: string;
  title: string;
  scripture_reference: string;
}

export interface Reflection {
  id: number;
  user_id: number;
  devotion_id: number;
  content: string;
  is_shared: boolean;
  created_at: string;
  updated_at: string;
  user: DevotionUserInfo | null;
  devotion: DevotionSummary | null;
}

export interface CreateReflectionData {
  devotion_id: number;
  content: string;
  is_shared?: boolean;
}

// =============================================================================
// Journal types
// =============================================================================

export type JournalMood =
  | 'grateful'
  | 'peaceful'
  | 'joyful'
  | 'hopeful'
  | 'reflective'
  | 'struggling'
  | 'seeking';

export interface AttachedVerse {
  id: number; // JournalVerse id
  verse_id: number;
  book_name: string;
  chapter_number: number;
  verse_number: number;
  text: string;
  note: string | null;
  created_at: string;
}

export interface JournalEntry {
  id: number;
  title: string | null;
  content: string;
  mood: JournalMood | null;
  created_at: string;
  updated_at: string;
  verses: AttachedVerse[];
}

export interface JournalEntrySummary {
  id: number;
  title: string | null;
  content_preview: string;
  mood: JournalMood | null;
  verse_count: number;
  created_at: string;
}

export interface JournalStats {
  total_entries: number;
  total_verses_attached: number;
  entries_this_week: number;
  entries_this_month: number;
  mood_breakdown: Record<string, number>;
  streak_days: number;
}

export interface CreateJournalEntryData {
  title?: string;
  content: string;
  mood?: JournalMood;
  verse_ids?: number[];
}

export interface UpdateJournalEntryData {
  title?: string;
  content?: string;
  mood?: JournalMood;
}

export interface AttachVerseData {
  verse_id: number;
  note?: string;
}

// =============================================================================
// Reading Plan types
// =============================================================================

export type ReadingPlanCategory = 'general' | 'gospel' | 'epistles' | 'ot' | 'nt' | 'topical';
export type ReadingPlanDifficulty = 'easy' | 'medium' | 'challenging';

export interface PassageReference {
  book: string;
  chapter: number;
  verses: string | null;
}

export interface ReadingPlanDay {
  id: number;
  plan_id: number;
  day_number: number;
  title: string | null;
  passages: PassageReference[];
  devotional_text: string | null;
  reflection_prompt: string | null;
}

export interface ReadingPlanSummary {
  id: number;
  name: string;
  description: string | null;
  duration_days: number;
  category: ReadingPlanCategory;
  difficulty: ReadingPlanDifficulty;
  is_public: boolean;
  is_featured: boolean;
  subscriber_count: number;
}

export interface ReadingPlan extends ReadingPlanSummary {
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
  days: ReadingPlanDay[];
}

export interface UserReadingPlan {
  id: number;
  user_id: number;
  plan_id: number;
  plan_name: string;
  plan_duration: number;
  started_at: string;
  current_day: number;
  last_read_at: string | null;
  completed_at: string | null;
  is_active: boolean;
  progress_percentage: number;
  days_completed: number;
}

export interface UserReadingPlanDetail extends UserReadingPlan {
  plan: ReadingPlanSummary;
  todays_reading: ReadingPlanDay | null;
}

export interface ReadingPlanStats {
  active_plans: number;
  completed_plans: number;
  total_days_read: number;
  current_streak: number;
  longest_streak: number;
  total_reading_time_minutes: number;
}

export interface ReadingPlanCategoryInfo {
  category: string;
  count: number;
  label: string;
}

export interface FeaturedPlansResponse {
  featured: ReadingPlanSummary[];
  categories: ReadingPlanCategoryInfo[];
  popular: ReadingPlanSummary[];
}

// =============================================================================
// Settings types
// =============================================================================

export type ChatRetention = 'view_once' | '24h' | '7d' | 'forever';

export interface UserSettings {
  user_id: number;

  // Media auto-save
  auto_save_sent: boolean;
  auto_save_received: boolean;
  story_auto_save: boolean;

  // Chat retention
  chat_retention: ChatRetention;

  // Notifications
  notifications_enabled: boolean;
  sound_enabled: boolean;
  vibration_enabled: boolean;

  // Privacy
  show_online_status: boolean;
  show_read_receipts: boolean;
  show_typing_indicators: boolean;
  allow_friend_requests: boolean;

  updated_at: string;
}

export interface UpdateSettingsData {
  auto_save_sent?: boolean;
  auto_save_received?: boolean;
  story_auto_save?: boolean;
  chat_retention?: ChatRetention;
  notifications_enabled?: boolean;
  sound_enabled?: boolean;
  vibration_enabled?: boolean;
  show_online_status?: boolean;
  show_read_receipts?: boolean;
  show_typing_indicators?: boolean;
  allow_friend_requests?: boolean;
}

export interface ChatRetentionOption {
  value: ChatRetention;
  label: string;
}

// =============================================================================
// Bible AI types
// =============================================================================

export type AIPersonality = 'paul' | 'priscilla' | 'ezra';

export interface BibleAIMessage {
  id: number;
  conversation_id: number;
  role: 'user' | 'assistant';
  content: string;
  verse_reference: string | null;
  verse_text: string | null;
  image_url: string | null;
  model_used: string | null;
  tokens_used: number | null;
  created_at: string;
}

export interface BibleAIConversation {
  id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  messages: BibleAIMessage[];
}

export interface BibleAIConversationSummary {
  id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  message_count: number;
  last_message_preview: string | null;
}

export interface BibleAIChatResponse {
  conversation_id: number;
  user_message: BibleAIMessage;
  assistant_message: BibleAIMessage;
  conversation_title: string | null;
}

export interface BibleAIQuickAskResponse {
  answer: string;
  model_used: string;
  tokens_used: number | null;
}

export interface BibleAIStatus {
  available: boolean;
  model: string;
  model_info: Record<string, unknown> | null;
  vision_available: boolean;
  vision_model: string | null;
}

// Drawing types
/** A point on the PICTURE, 0-1 on each axis — MEDIA SPACE, see `TextBar`. */
export interface DrawingPoint {
  x: number;  // 0-1 fraction of the media's width
  y: number;  // 0-1 fraction of the media's height
}

export interface DrawingStroke {
  id: string;
  points: DrawingPoint[];
  color: string;      // hex color
  /**
   * Line width as a fraction of the PICTURE's width — MEDIA SPACE, like every
   * other overlay number (BUG-112). It used to be `brushSize`, a count of
   * SCREEN pixels, which was then replayed onto a 1080-wide file: an 8px brush
   * is 2.05% of a 390px preview but 0.74% of the file, so every drawing
   * arrived ~2.8x thinner than it was drawn. A fraction is the same mark at
   * any size, on any screen.
   */
  w: number;
  timestamp: number;  // for ordering
}

// =============================================================================
// Passkey/WebAuthn types
// =============================================================================

export interface PasskeyDevice {
  id: number;
  device_name: string;
  device_type: string | null;
  last_used_at: string | null;
  created_at: string;
}

/** One signed-in session (FEAT-013). `id` is a server-side hash — raw
 * session tokens never reach page JS. */
export interface SessionInfo {
  id: string;
  device: string;
  created: string | null;
  last_seen: string | null;
  via: 'passkey' | 'recovery' | 'register' | null;
  current: boolean;
}

export interface InviteCodeInfo {
  code: string;
  display_name: string;
  expires_at: string;
}

export interface PublicKeyCredentialCreationOptionsJSON {
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ type: 'public-key'; id: string; transports?: string[] }>;
  authenticatorSelection?: {
    authenticatorAttachment?: 'platform' | 'cross-platform';
    residentKey?: 'discouraged' | 'preferred' | 'required';
    userVerification?: 'discouraged' | 'preferred' | 'required';
  };
  attestation?: 'none' | 'indirect' | 'direct' | 'enterprise';
}

export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{ type: 'public-key'; id: string; transports?: string[] }>;
  userVerification?: 'discouraged' | 'preferred' | 'required';
}

export interface PasskeyRegistrationOptions {
  user_id: number;
  username: string;
  display_name: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}

export interface PasskeyAuthOptions {
  options: PublicKeyCredentialRequestOptionsJSON;
  challenge_id: string;
}

export interface PasskeyAuthResponse {
  access_token: string;
  token_type: string;
  user: UserPrivate;
}

export interface PasskeyRegisterResponse {
  access_token: string;
  token_type: string;
  user: UserPrivate;
  recovery_codes: string[];
}

